<template>
  <div class="accordion mt-2">
    <div class="accordion-item">
      <!--
        O nível do cabeçalho é CONFIGURÁVEL (`nivel`), com 2 como padrão para não mudar as telas
        que já usam o componente.

        Sem isso, um acordeão aninhado dentro de outro repetia `<h2>` em todos os níveis, e a
        auditoria estrutural acusa salto de cabeçalho — o leitor de tela perde a noção de qual
        seção está dentro de qual, que é exatamente o que a modularização quer resolver.
      -->
      <component :is="`h${nivel}`" class="accordion-header">
        <button
          class="accordion-button"
          :class="[
            simples ? 'accordion-button-simples' : `text-white ${bgColor}`,
            { collapsed: !defaultOpen },
          ]"
          type="button"
          data-bs-toggle="collapse"
          :data-bs-target="'#' + targetId"
          :aria-expanded="defaultOpen ? 'true' : 'false'"
          :aria-controls="targetId"
        >
          <i v-if="iconClass" :class="iconClass" class="me-2" aria-hidden="true"></i>
          {{ title }}

          <!--
            Contador opcional ao lado do título — "3 pendências", "18 disciplinas".

            Com tudo fechado por padrão, o usuário não sabe onde há conteúdo sem abrir cada
            seção; este é o resumo que evita a caça.
          -->
          <span v-if="resumo" class="badge bg-light text-dark ms-2 fw-normal">{{ resumo }}</span>
        </button>
      </component>

      <div :id="targetId" class="accordion-collapse collapse" :class="{ show: defaultOpen }">
        <div class="accordion-body">
          <slot></slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
  import { defineProps } from 'vue';

  defineProps({
    title: String,
    targetId: String,
    iconClass: String,
    bgColor: {
      type: String,
      default: 'bg-primary',
    },
    /** Define se o acordeão inicia aberto ou fechado. */
    defaultOpen: Boolean,
    /**
     * O nível do cabeçalho (2 a 6). Dois por padrão, que é o que as telas existentes já usavam.
     *
     * Acordeão aninhado tem de descer um nível: dentro de um `nivel="3"`, o de dentro é 4.
     */
    nivel: {
      type: Number,
      default: 2,
    },
    /**
     * Estilo discreto, para os níveis internos.
     *
     * O padrão pinta o cabeçalho de `bg-primary` com texto branco, o que funciona no nível de
     * cima e vira uma parede de cor quando repetido em três níveis. Com `simples` o cabeçalho
     * usa a cor padrão do tema.
     */
    simples: Boolean,
    /** Texto curto ao lado do título (contagem, estado). */
    resumo: String,
  });
</script>

<style scoped>
  .accordion-button {
    font-weight: bold;
  }

  /*
   * O texto branco vale só para a variante colorida. Antes era `!important` para os dois
   * estados, o que pintava de branco também o cabeçalho discreto — invisível sobre o fundo
   * claro do tema.
   */
  .accordion-button.text-white.collapsed,
  .accordion-button.text-white:not(.collapsed) {
    color: white !important;
  }

  /* Variante discreta: fundo do tema, texto do tema, e a seta continua indicando o estado. */
  .accordion-button-simples {
    background-color: var(--bs-tertiary-bg, #f6f7fb);
    color: inherit;
  }

  .accordion-button-simples:not(.collapsed) {
    background-color: var(--bs-secondary-bg, #eef0f6);
    color: inherit;
    box-shadow: none;
  }
</style>
